import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { TargetAndTransition, Transition } from "framer-motion";
import { useListInsertion } from "@tokens/index";

/**
 * Insertion motion bound to one measured row.
 *
 * A new row is measured once in a layout effect and grows from 0 to that px
 * (`insertionMotion`). Until the measurement lands, `animate` is the
 * `initial` object itself, which framer treats as "nothing to animate", so
 * the first painted frame is already the start of the real animation and no
 * throwaway animation completes early. After the row settles the target
 * becomes `"auto"` so later content changes flow. Rows that were present at
 * mount get `initial: false` and never animate.
 *
 * Put `contentRef` on the element that wraps the row content: the row itself
 * starts at height 0, so only its content has a height to measure. Clip the
 * row while `growing`, and only then, so a focus ring is never cut at rest.
 */
export interface MeasuredInsertion {
  /** Spread onto the `motion.*` row. */
  readonly motionProps: {
    readonly initial: TargetAndTransition | false;
    readonly animate: TargetAndTransition;
    readonly exit: TargetAndTransition;
    readonly transition: Transition;
    readonly onAnimationComplete: () => void;
  };
  readonly contentRef: RefObject<HTMLDivElement>;
  readonly growing: boolean;
}

export function useMeasuredInsertion(isNew: boolean): MeasuredInsertion {
  const contentRef = useRef<HTMLDivElement>(null);
  const [growing, setGrowing] = useState(isNew);
  const [measured, setMeasured] = useState<number | null>(null);
  const insertion = useListInsertion(growing && measured !== null ? measured : "auto");

  useLayoutEffect(() => {
    if (!growing || measured !== null) return;
    const el = contentRef.current;
    if (el !== null) setMeasured(el.getBoundingClientRect().height);
  }, [growing, measured]);

  const awaitingMeasure = growing && measured === null;
  return {
    motionProps: {
      initial: isNew ? insertion.initial : false,
      animate: awaitingMeasure ? insertion.initial : insertion.animate,
      exit: insertion.exit,
      transition: insertion.transition,
      onAnimationComplete: () => setGrowing(false),
    },
    contentRef,
    growing,
  };
}
